import type { DtActionsAtom } from '@/components/pages/backoffice/_klb-penyuluh/_kecamatan/state/atoms'
import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { SarprasDataType } from './types'
import type { MutationSarprasValues } from './validations'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['sarpras', key] as const

const getAll = (
  params?: DtActionsAtom
): ServiceQueryOptionType<ServiceGetAllResponse<Array<SarprasDataType>>> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<SarprasDataType>>
      >(`kelembagaan/bpp/${params?.id_bpp}/sarana-prasarana`, {
        params: {
          page: params?.page,
          per_page: params?.perPage || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string,
  id_bpp?: string
): ServiceQueryOptionType<ServiceGetByIdResponse<SarprasDataType>> => ({
  queryKey: QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<SarprasDataType>>(
        `kelembagaan/bpp/${id_bpp ?? ''}/sarana-prasarana/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<SarprasDataType>,
  MutationSarprasValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationSarprasValues) => {
    try {
      const response = await api.post(
        `kelembagaan/bpp/${data.id_bpp ?? ''}/sarana-prasarana`,
        {
          ...data,
          id_sarana_prasarana: data.id_sarana_prasarana.value,
          id_jenis_pembiayaan: data.id_jenis_pembiayaan.value
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<SarprasDataType>,
  MutationSarprasValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationSarprasValues) => {
    try {
      const response = await api.put<ServiceGetByIdResponse<SarprasDataType>>(
        `kelembagaan/bpp/${data?.id_bpp ?? ''}/sarana-prasarana/${data.id_sarpras}`,
        {
          ...data,
          id_sarana_prasarana: data.id_sarana_prasarana.value,
          id_jenis_pembiayaan: data.id_jenis_pembiayaan.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<SarprasDataType>,
  { id: string; id_bpp: string }
> => ({
  mutationFn: async (data: { id: string; id_bpp: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<SarprasDataType>
      >(`kelembagaan/bpp/${data?.id_bpp ?? ''}/sarana-prasarana/${data.id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const sarpras = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData
}
